import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Inbox, Mail, MailOpen, Send, BookOpen, FileText, Shield, Route, ClipboardCheck, ExternalLink, Filter } from "lucide-react";
import api from "../../lib/api";


const typeIcons = { course: BookOpen, report: FileText, assessment: ClipboardCheck, learning_path: Route, compliance: Shield };
const typeColors = { course: "#1B4F8A", report: "#D97706", assessment: "#7C3AED", learning_path: "#0E8A55", compliance: "#C7322A" };

const SharedInboxPage = () => {
  const { token, user } = useAuth();
  const layoutVariant = user?.role === "super_admin" ? "super_admin" : user?.role === "manager" ? "manager" : user?.role === "learner" ? "learner" : "tenant_admin";
  const [items, setItems] = useState([]);
  const [sentItems, setSentItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("inbox");
  const [filterType, setFilterType] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [shareForm, setShareForm] = useState({ item_type: "course", item_id: "", shared_with_emails: "", message: "" });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("item_type", filterType);
      const [inboxRes, sentRes] = await Promise.all([
        api.get("/shared-items/inbox?${params}"),
        api.get("/shared-items/sent"),
      ]);
      setItems(inboxRes.data.items || []);
      setUnreadCount(inboxRes.data.unread_count || 0);
      setSentItems(sentRes.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRead = async (id) => {
    try {
      await api.post("/shared-items/${id}/read", {});
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleShare = async () => {
    if (!shareForm.item_id) return toast.error("Enter an item ID");
    const emails = shareForm.shared_with_emails.split(",").map(e => e.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Enter at least one email");
    try {
      await api.post("/shared-items/send", {
        ...shareForm,
        shared_with_emails: emails,
      });
      toast.success("Item shared successfully");
      setShowShare(false);
      setShareForm({ item_type: "course", item_id: "", shared_with_emails: "", message: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to share"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  const displayItems = activeTab === "inbox" ? items : sentItems;

  return (
    <AdminLayout title="Shared With Me" variant={layoutVariant}>
    <div className="p-6 space-y-6" data-testid="shared-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Shared With Me</h1>
          <p className="text-sm text-muted-foreground mt-1">Content shared with you by colleagues and administrators</p>
        </div>
        <Button onClick={() => setShowShare(true)} className="btn-primary" data-testid="share-item-btn">
          <Send className="w-4 h-4 mr-2" /> Share Content
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #1B4F8A" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Total Received</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{items.length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #F5841F" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Unread</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{unreadCount}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #0E8A55" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Sent</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{sentItems.length}</p>
        </div>
      </div>

      {/* Tabs + Filter */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {[{ key: "inbox", label: "Inbox", icon: Inbox }, { key: "sent", label: "Sent", icon: Send }].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === tab.key ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.key === "inbox" && unreadCount > 0 && <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{unreadCount}</span>}
            </button>
          ))}
        </div>
        <select className="h-9 rounded-md border px-3 text-sm bg-background" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="course">Courses</option>
          <option value="assessment">Assessments</option>
          <option value="compliance">Compliance</option>
          <option value="learning_path">Learning Paths</option>
          <option value="report">Reports</option>
        </select>
      </div>

      {/* Share Modal */}
      {showShare && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowShare(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Share Content</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Content Type</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={shareForm.item_type} onChange={e => setShareForm({...shareForm, item_type: e.target.value})}>
                  <option value="course">Course</option>
                  <option value="assessment">Assessment</option>
                  <option value="compliance">Compliance</option>
                  <option value="learning_path">Learning Path</option>
                  <option value="report">Report</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Item ID</label>
                <Input value={shareForm.item_id} onChange={e => setShareForm({...shareForm, item_id: e.target.value})} placeholder="Paste the item ID" className="mt-1" data-testid="share-item-id" />
              </div>
              <div>
                <label className="text-sm font-medium">Recipient Emails (comma-separated)</label>
                <Input value={shareForm.shared_with_emails} onChange={e => setShareForm({...shareForm, shared_with_emails: e.target.value})} placeholder="user1@email.com, user2@email.com" className="mt-1" data-testid="share-emails" />
              </div>
              <div>
                <label className="text-sm font-medium">Message (optional)</label>
                <Input value={shareForm.message} onChange={e => setShareForm({...shareForm, message: e.target.value})} placeholder="Check this out!" className="mt-1" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowShare(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleShare} data-testid="confirm-share-btn">Share</Button>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-2">
        {displayItems.length === 0 ? (
          <div className="border rounded-xl p-8 text-center text-muted-foreground">
            <Inbox className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">{activeTab === "inbox" ? "No shared items yet" : "You haven't shared anything yet"}</p>
            <p className="text-sm mt-1">{activeTab === "inbox" ? "When someone shares content with you, it'll appear here" : "Use the Share button to send content to colleagues"}</p>
          </div>
        ) : displayItems.map(item => {
          const TypeIcon = typeIcons[item.item_type] || FileText;
          const color = typeColors[item.item_type] || "#1B4F8A";
          const isUnread = activeTab === "inbox" && !item.is_read;
          return (
            <div key={item.id}
              className={`border rounded-xl p-4 flex items-center gap-4 transition-colors cursor-pointer hover:bg-muted/30 ${isUnread ? 'bg-blue-50/50 border-blue-200' : ''}`}
              onClick={() => activeTab === "inbox" && !item.is_read && markRead(item.id)}
              data-testid={`shared-item-${item.id}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
                <TypeIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`font-semibold text-sm truncate ${isUnread ? '' : 'text-muted-foreground'}`}>{item.item_title || item.item_id}</p>
                  {isUnread && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {activeTab === "inbox" ? `From ${item.shared_by_name}` : `To user`} · {item.item_type.replace("_", " ")} · {new Date(item.created_at).toLocaleDateString()}
                </p>
                {item.message && <p className="text-xs text-muted-foreground mt-1 italic">"{item.message}"</p>}
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase" style={{ background: `${color}15`, color }}>{item.item_type.replace("_", " ")}</span>
            </div>
          );
        })}
      </div>
    </div>
    </AdminLayout>
  );
};

export default SharedInboxPage;
